import { Effect, Exit, Fiber } from "effect"
import { WorkspaceContext } from "@/control-plane/workspace-context"
import { Instance, type InstanceContext } from "@/project/instance"
import type { WorkspaceID } from "@/control-plane/schema"
import { LocalContext } from "@/util/local-context"
import { InstanceRef, WorkspaceRef } from "./instance-ref"
import { attachWith } from "./run-service"
import * as LogContext from "@opencode-ai/core/util/log-context" // infcode_change - propagate CLI session log correlation
// infcode_change start
import { DirectorySessionContext, DirectorySessionRef, type DirectorySessionBinding, WorkspaceSessionContext, WorkspaceSessionRef, type WorkspaceSessionBinding } from "@/infcode/session/execution-context"
import { workspaceActivity, WorkspaceActivityRef, type WorkspaceActivityLease } from "@/infcode/session/workspace-activity"
// infcode_change end

export interface Shape {
  readonly promise: <A, E, R>(effect: Effect.Effect<A, E, R>) => Promise<A>
  readonly fork: <A, E, R>(effect: Effect.Effect<A, E, R>) => Fiber.Fiber<A, E>
  readonly run: <A, E, R>(effect: Effect.Effect<A, E, R>) => Effect.Effect<A, E>
  readonly withSessionLogContext: <A, E, R>(sessionID: string, effect: Effect.Effect<A, E, R>) => Effect.Effect<A, E> // infcode_change - execute the full Effect lifecycle under session ALS
}

// infcode_change start
function restore<R>(instance: InstanceContext | undefined, workspace: WorkspaceID | undefined, fn: () => R,
  workspaceSession?: WorkspaceSessionBinding, directorySession?: DirectorySessionBinding, activity?: WorkspaceActivityLease): R {
  return workspaceActivity.restore(activity, () => DirectorySessionContext.restore(directorySession, () =>
    WorkspaceSessionContext.restore(workspaceSession, () => restoreInstance(instance, workspace, fn)),
  ))
}

function restoreInstance<R>(instance: InstanceContext | undefined, workspace: WorkspaceID | undefined, fn: () => R): R {
// infcode_change end
  if (instance && workspace !== undefined) {
    return WorkspaceContext.restore(workspace, () => Instance.restore(instance, fn))
  }
  if (instance) return Instance.restore(instance, fn)
  if (workspace !== undefined) return WorkspaceContext.restore(workspace, fn)
  return fn()
}

/**
 * Bridge from Effect into a Promise-returning JS callback while installing
 * legacy `Instance.context` and `WorkspaceContext` AsyncLocalStorage for
 * the duration of the callback. Effect's `InstanceRef`/`WorkspaceRef` do
 * not propagate across async/await boundaries inside `Effect.promise(() =>
 * async fn)` callbacks that re-enter Effect via `AppRuntime.runPromise`,
 * but Node's AsyncLocalStorage does. Use this whenever an Effect crosses
 * into JS that may itself spawn new Effect runtimes (workspace adapters,
 * legacy plugins, etc.).
 *
 * Mirrors `Effect.promise` but restores legacy ALS first.
 */
// infcode_change start - hold actual Promise work until its cleanup completes.
export const fromPromise = <T>(fn: () => Promise<T> | T): Effect.Effect<T> =>
  Effect.gen(function* () {
    const instance = yield* InstanceRef
    const workspace = yield* WorkspaceRef
    const workspaceSession = yield* WorkspaceSessionRef // infcode_change
    const directorySession = yield* DirectorySessionRef // infcode_change
    const activity = (yield* WorkspaceActivityRef) ?? workspaceActivity.current() // infcode_change
    return yield* Effect.promise(() => activity
      ? workspaceActivity.run(activity.workspaceID, async () => restore(instance, workspace, fn, workspaceSession, directorySession, workspaceActivity.current()), activity)
      : Promise.resolve(restore(instance, workspace, fn, workspaceSession, directorySession))) // infcode_change
  })
// infcode_change end

// infcode_change start - preserve instance/workspace ALS while retaining typed Promise error mapping
export const fromTryPromise = <T, E>(options: {
  readonly try: () => PromiseLike<T>
  readonly catch: (error: unknown) => E
}): Effect.Effect<T, E> =>
  Effect.gen(function* () {
    const instance = yield* InstanceRef
    const workspace = yield* WorkspaceRef
    const workspaceSession = yield* WorkspaceSessionRef
    const directorySession = yield* DirectorySessionRef
    const activity = (yield* WorkspaceActivityRef) ?? workspaceActivity.current()
    return yield* Effect.tryPromise({
      try: () => activity
        ? workspaceActivity.run(activity.workspaceID, async () => restore(instance, workspace, options.try, workspaceSession, directorySession, workspaceActivity.current()), activity)
        : restore(instance, workspace, options.try, workspaceSession, directorySession),
      catch: options.catch,
    })
  })
// infcode_change end

export function make(): Effect.Effect<Shape> {
  return Effect.gen(function* () {
    const ctx = yield* Effect.context()
    const value = yield* InstanceRef
    const instance =
      value ??
      (() => {
        try {
          return Instance.current
        } catch (err) {
          if (!(err instanceof LocalContext.NotFound)) throw err
        }
      })()
    const workspace = (yield* WorkspaceRef) ?? WorkspaceContext.workspaceID
    // infcode_change start
    const workspaceSession = (yield* WorkspaceSessionRef) ?? WorkspaceSessionContext.current()
    const directorySession = (yield* DirectorySessionRef) ?? DirectorySessionContext.current()
    const activity = (yield* WorkspaceActivityRef) ?? workspaceActivity.current()
    const restoreAll = <A>(run: () => A) => restore(instance, workspace, run, workspaceSession, directorySession, activity)
    const attach = <A, E, R>(effect: Effect.Effect<A, E, R>) => attachWith(effect, { instance, workspace, workspaceSession, directorySession })
    // infcode_change end
    const wrap = <A, E, R>(effect: Effect.Effect<A, E, R>) =>
      attach(effect).pipe(Effect.provide(ctx)) as Effect.Effect<A, E, never>

    return {
      promise: <A, E, R>(effect: Effect.Effect<A, E, R>) =>
        restoreAll(() => Effect.runPromise(wrap(effect))), // infcode_change
      fork: <A, E, R>(effect: Effect.Effect<A, E, R>) =>
        restoreAll(() => Effect.runFork(wrap(effect))), // infcode_change
      run: <A, E, R>(effect: Effect.Effect<A, E, R>) =>
        Effect.callback<A, E>((resume) => {
          restoreAll(() => // infcode_change
            Effect.runPromiseExit(wrap(effect)).then((exit) =>
              resume(Exit.isSuccess(exit) ? Effect.succeed(exit.value) : Effect.failCause(exit.cause)),
            ),
          )
        }),
      // infcode_change start - run the child Effect runtime inside the session ALS context.
      // Wrapping only Effect construction would end the context before lazy Effect execution starts.
      withSessionLogContext: <A, E, R>(sessionID: string, effect: Effect.Effect<A, E, R>) =>
        Effect.callback<A, E>((resume) => {
          const parent = LogContext.current()
          const childBinding = directorySession ? Object.freeze({ ...directorySession, sessionID }) : undefined
          const fiber = LogContext.provide({ sessionID }, () =>
            restore(instance, workspace, () => Effect.runFork(wrap(
              effect.pipe(Effect.provideService(DirectorySessionRef, childBinding)),
            )), workspaceSession, childBinding, activity),
          )
          const removeObserver = fiber.addObserver((exit) => {
            LogContext.restore(parent, () =>
              resume(Exit.isSuccess(exit) ? Effect.succeed(exit.value) : Effect.failCause(exit.cause)),
            )
          })
          return Effect.suspend(() => {
            removeObserver()
            return Fiber.interrupt(fiber)
          })
        }),
      // infcode_change end
    } satisfies Shape
  })
}

export * as EffectBridge from "./bridge"
